import React from "react";
import { useState, useEffect } from "react";
import { Dialog, Box, Paper, Typography, TextField, Button, CircularProgress, Tooltip } from "@mui/material";
import { Toolbar } from "@mui/material";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { List } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import {
  ApiGet,
  ApiPost,
  ApiPut,
} from "../helpers/API/ApiData";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";

import { SelfHostedEditor } from "../components/TextEditor";
import { PageHeader } from "../components/PageHeader";
import { createSanitizedMarkup } from "../utils/createSanitizedMarkup";
import { DataTable, TableColumnDef } from "../components/DataTable";
import { useContentLanguage } from "../hooks";
import { LanguageTabs } from "../components/LanguageTabs";

interface EditorState {
  content: string;
  content_ger: string;
}

export default function AboutApp() {
  const [aboutAppData, setAboutAppData] = useState<any>({});
  const [addAboutApp, setAddAboutApp] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllAbout, setGetAllAbout] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [aboutIdForEdit, setAboutIdForEdit] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const [lang, setLang] = useContentLanguage();

  const [editorState, setEditorState] = useState<EditorState>({
    content: "",
    content_ger: "",
  });

  useEffect(() => {
    getAllImprintData();
  }, [page, countPerPage, search]);

  const addAboutModelClose = (_e: any) => {
    setAddAboutApp(false);
    setIsEditApi(false);
    setErrors({});
    setAboutAppData({});
    setEditorState({ content: "", content_ger: "" });
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setAboutAppData({ ...aboutAppData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const errors: any = {};
    if (!aboutAppData?.aboutAppName_ger?.trim()) {
      formIsValid = false;
      errors["aboutAppName_ger"] = "*Bitte geben Sie den Namen ein (DE)";
    }
    if (!aboutAppData?.aboutAppName?.trim()) {
      formIsValid = false;
      errors["aboutAppName"] = "*Bitte geben Sie den Namen ein (EN)";
    }
    if (!editorState?.content_ger) {
      formIsValid = false;
      errors["aboutAppDescription_ger"] = "*Bitte geben Sie eine Beschreibung ein (DE)";
    }
    if (!editorState?.content) {
      formIsValid = false;
      errors["aboutAppDescription"] = "*Bitte geben Sie eine Beschreibung ein (EN)";
    }
    setErrors(errors);
    return formIsValid;
  };

  const getAllImprintData = async () => {
    setLoaderForGetAll(true);
    const searchParam = search ? `&letter=${search}` : "";
    await ApiGet(`aboutApp/find?page=${page}&limit=${countPerPage}${searchParam}`)
      .then((res: any) => {
        setGetAllAbout(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const addAboutData = async (_e: any) => {
    setIsEditApi(false);
    if (formValidation()) {
      setLoader(true);
      const data = {
        aboutAppName: aboutAppData?.aboutAppName,
        aboutAppName_ger: aboutAppData?.aboutAppName_ger,
        aboutAppDescription: editorState?.content,
        aboutAppDescription_ger: editorState?.content_ger,
      };

      await ApiPost(`aboutApp/create`, data)
        .then(() => {
          toast.success("Über App fügen Sie erfolgreich hinzu");
          setAddAboutApp(false);
          setIsEditApi(false);
          getAllImprintData();
          setAboutAppData({});
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const editAboutData = async (_e: any) => {
    if (formValidation()) {
      setLoader(true);
      const data = {
        aboutAppName: aboutAppData?.aboutAppName,
        aboutAppName_ger: aboutAppData?.aboutAppName_ger,
        aboutAppDescription: editorState?.content,
        aboutAppDescription_ger: editorState?.content_ger,
      };

      await ApiPut(`aboutApp/update?id=${aboutIdForEdit}`, data)
        .then(() => {
          toast.success("Über App erfolgreich aktualisiert");
          getAllImprintData();
          setAddAboutApp(false);
          setIsEditApi(false);
          setAboutAppData({});
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  // Content is sanitized via the createSanitizedMarkup utility (uses DOMPurify)
  const columns: TableColumnDef[] = [
    {
      header: "Name",
      render: (row: any) => (lang === "de" ? row?.aboutAppName_ger : row?.aboutAppName) || "-",
      getValue: (row: any) => lang === "de" ? row?.aboutAppName_ger : row?.aboutAppName,
    },
    {
      header: "Datum",
      render: (row: any) => moment(row?.createdAt).format("DD.MM.YYYY"),
      getValue: (row: any) => row?.createdAt,
    },
    {
      header: "Einstellungen",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="bearbeiten" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setIsEditApi(true);
                setAddAboutApp(true);
                setAboutIdForEdit(row?._id);
                setAboutAppData({
                  aboutAppName: row?.aboutAppName,
                  aboutAppName_ger: row?.aboutAppName_ger,
                });
                setEditorState({
                  content: row?.aboutAppDescription || "",
                  content_ger: row?.aboutAppDescription_ger || "",
                });
              }}
            >
              <CreateIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="Über Die App"
            onSearch={(val) => { setSearch(val); setPage(1); }}
          />
        </Box>
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllAbout || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      {addAboutApp && (
        <Dialog fullScreen open={addAboutApp}>
          <Toolbar>
            <IconButton edge="end" color="inherit" onClick={addAboutModelClose} aria-label="close" sx={{ ml: "auto" }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Deutsch</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>English</Typography>
                </Box>
              </Box>

              {/* Name row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Titel</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="aboutAppName_ger"
                    value={aboutAppData?.aboutAppName_ger || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors["aboutAppName_ger"]}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="aboutAppName"
                    value={aboutAppData?.aboutAppName || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors["aboutAppName"]}</Typography>
                </Box>
              </Box>

              {/* Description row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Beschreibung</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content_ger: editor?.getContent() }));
                    }}
                    initialValue={editorState.content_ger}
                  />
                  <Typography variant="caption" color="error">{errors["aboutAppDescription_ger"]}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content: editor?.getContent() }));
                    }}
                    initialValue={editorState.content}
                  />
                  <Typography variant="caption" color="error">{errors["aboutAppDescription"]}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                disabled={loader}
                onClick={(e) => { isEditApi ? editAboutData(e) : addAboutData(e); }}
              >
                {isEditApi ? "Speichern" : "Hinzufügen"}
                {loader && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
